import React, { useMemo, useRef } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  alpha,
} from "@mui/material";
import { formatAmount } from "../../../../utils/hooks/dataHandler";
import { JustInfo } from "../../../utils/Icon";
import { JustSwitch } from "../../../utils/Form/Switch";
import { BalanceVerification } from "../../utils/ScheduleContent";
import {
  CameraExportButton,
  exportComponentAsImage,
} from "../../../../utils/export/componentExporter";

const totalRowStyle = {
  fontWeight: 700,
  color: "text.primary",
  py: 2,
  borderTop: "2px solid",
  borderColor: (theme) => alpha(theme.palette.divider, 0.3),
  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
};

const iaRowStyle = {
  fontWeight: 700,
  color: "text",
  py: 2,
  borderTop: "2px dashed",
  borderColor: (theme) => alpha(theme.palette.primary.light, 0.4),
  bgcolor: (theme) => alpha(theme.palette.secondary.light, 0.09),
};

// Reusable function to calculate total principal paid
export const calculateTotalPrincipalPaid = (principalPaid) => {
  if (!principalPaid || !Array.isArray(principalPaid)) return 0;
  return principalPaid.reduce(
    (sum, p) => sum + (Math.round(parseFloat(p.amount)) || 0),
    0
  );
};

export const calculatePrincipalYearTotal = (entries, isS19) => {
  if (!Array.isArray(entries)) return 0;

  if (isS19) {
    return entries.reduce((sum, entry) => {
      if (entry.currentYearDisposed || entry.disposed) {
        return sum;
      }

      const aa = entry.deferred ? 0 : entry.aa || 0;
      const ia = entry.ia || 0;
      return sum + aa + ia;
    }, 0);
  }

  return entries.reduce((sum, entry) => sum + (entry.amount || 0), 0);
};

export const getAaClaimForPrincipalYear = (
  principalDetails,
  year,
  { isCurrentYearDisposed = false, yearOfAssessment } = {}
) => {
  const entriesForPrincipalYear = Object.values(principalDetails || {})
    .flat()
    .filter((entry) => entry?.from === year);

  const hasForfeitedClaim = entriesForPrincipalYear.some(
    (entry) => entry.disposed || entry.currentYearDisposed
  );

  if (
    hasForfeitedClaim ||
    (isCurrentYearDisposed && year === yearOfAssessment)
  ) {
    return "Disposed";
  }

  const sameYearEntry = Array.isArray(principalDetails?.[year])
    ? principalDetails[year].find((entry) => entry.from === year)
    : null;
  const claimedEntry = entriesForPrincipalYear.find((entry) => !entry.deferred);

  return sameYearEntry?.aa || claimedEntry?.aa || 0;
};

export const calculateTotalAaClaims = (principalDetails) => {
  return Object.values(principalDetails || {})
    .flat()
    .reduce((sum, entry) => sum + (entry.amount || 0), 0);
};

const PrincipalPaidTable = ({
  selectedItem,
  deferredYears = [],
  yearOfAssessment,
  showDecimals = false,
  onShowDecimalsChange,
}) => {
  const exportRef = useRef(null);

  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(exportRef.current, selectedItem.assetName);
    }
  };

  const isS19 = selectedItem.caType === "S19";
  const shouldShowDecimals = isS19 && showDecimals;

  const principalPaidMap = useMemo(() => {
    const map = {};
    for (const { year, amount } of selectedItem.principalPaid || []) {
      map[year] = amount;
    }
    return map;
  }, [selectedItem.principalPaid]);

  if (!selectedItem?.principalPaid || selectedItem.principalPaid.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: "center",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
          border: "1px dashed",
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
          borderRadius: 2,
        }}
      >
        <Typography
          variant='body1'
          color='text.secondary'
          gutterBottom
          sx={{ mb: 1 }}
        >
          No principal paid records for this asset.
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Add principal paid details using the button above.
        </Typography>
      </Paper>
    );
  }

  const totalPrincipalPaid = calculateTotalPrincipalPaid(
    selectedItem.principalPaid
  );

  const allPrincipalYears = Object.keys(selectedItem.principalDetails || {})
    .map(Number)
    .filter(
      (year) =>
        !(
          isS19 === true &&
          selectedItem?.isCurrentYearDisposed === true &&
          year > yearOfAssessment
        )
    )
    .sort((a, b) => a - b);

  const yearTotals = {};
  const twdvTotals = {};
  const finalTotals = {};

  const principalPaidRows = isS19
    ? allPrincipalYears.map((year) => ({
        year,
        amount: principalPaidMap[year] || 0,
      }))
    : selectedItem.principalPaid;

  let lastTotal = 0;
  allPrincipalYears.forEach((year) => {
    if (selectedItem.isCurrentYearDisposed && year > yearOfAssessment) {
      return;
    }

    const entries = selectedItem.principalDetails?.[year] || [];

    let total = calculatePrincipalYearTotal(entries, isS19);
    yearTotals[year] = total;

    const cur_pp = principalPaidMap[year] || 0;

    let netTotal = lastTotal + cur_pp - total;
    twdvTotals[year] = lastTotal;
    finalTotals[year] = netTotal;

    lastTotal = netTotal;
  });

  const isYearDeferred = (year) => {
    return deferredYears.includes(year);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        {/* {isS19 && (
          <JustSwitch
            checked={showDecimals}
            label='Show decimals'
            onChange={(event) => onShowDecimalsChange?.(event.target.checked)}
          />
        )} */}
        <CameraExportButton
          onClick={handleExportAsImage}
          tooltipTitle='Screenshot of the principal paid table'
        />
      </Box>
      {/* Principal Paid Over Cost Hint */}
      {selectedItem?.cost &&
      totalPrincipalPaid !== parseFloat(selectedItem.cost) ? (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 1,
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.warning.main, 0.2),
            borderRadius: 2,
          }}
        >
          <Typography
            variant='body2'
            fontWeight={600}
            sx={{ color: (theme) => theme.palette.warning.main }}
          >
            Warning: The total principal paid not matched with the asset cost.
          </Typography>
        </Paper>
      ) : null}
      {isS19 && !selectedItem.initialAllowanceRate ? (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 1,
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.error.main, 0.2),
            borderRadius: 2,
          }}
        >
          <Typography variant='body2' color='error' fontWeight={600}>
            Initial allowance rate is not provided. Please provide an initial
            allowance rate for S19 assets to ensure calculations are complete.
          </Typography>
        </Paper>
      ) : null}
      <TableContainer
        ref={exportRef}
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: (theme) => alpha(theme.palette.divider, 0.1),
          overflow: "auto",
          maxWidth: "100%",
          "& .MuiTable-root": {
            minWidth: "max-content",
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
              }}
            >
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                  py: 2,
                }}
              >
                Principal year
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                  py: 2,
                  textAlign: "center",
                }}
              >
                Principal Paid
              </TableCell>
              {isS19 && (
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    borderBottom: "2px solid",
                    borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                    py: 2,
                    textAlign: "center",
                    borderRight: "2px solid",
                    borderRightColor: (theme) =>
                      alpha(theme.palette.divider, 0.1),
                  }}
                >
                  AA Claims
                </TableCell>
              )}
              {allPrincipalYears.map((year, index) => (
                <TableCell
                  key={index}
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    borderBottom: "2px solid",
                    borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                    py: 2,
                    textAlign: "center",
                  }}
                >
                  {year}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {principalPaidRows.map((row, rowIdx) => (
              <TableRow key={rowIdx}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    py: 2,
                  }}
                >
                  {row.year}
                </TableCell>

                <TableCell align='center'>{formatAmount(row.amount)}</TableCell>

                {isS19 && (
                  <TableCell
                    align='center'
                    sx={{
                      borderRight: "2px solid",
                      borderRightColor: (theme) =>
                        alpha(theme.palette.divider, 0.1),
                    }}
                  >
                    {(() => {
                      const aaClaim = getAaClaimForPrincipalYear(
                        selectedItem?.principalDetails,
                        row.year,
                        {
                          isCurrentYearDisposed:
                            selectedItem?.isCurrentYearDisposed,
                          yearOfAssessment,
                        }
                      );

                      return typeof aaClaim === "string"
                        ? aaClaim
                        : formatAmount(aaClaim, !shouldShowDecimals);
                    })()}
                  </TableCell>
                )}

                {allPrincipalYears.map((colYear, colIdx) => {
                  const details =
                    selectedItem.principalDetails?.[colYear] || [];
                  const match = details.find(
                    (entry) => entry.from === row.year
                  );
                  const isDeferredRecord =
                    match?.deferred || isYearDeferred(colYear);

                  let displayAmount = 0;
                  if (isDeferredRecord) displayAmount = "Deferred";
                  else if (
                    match?.disposed ||
                    (selectedItem.isCurrentYearDisposed &&
                      colYear === yearOfAssessment)
                  ) {
                    displayAmount = "Disposed";

                    if (colYear > yearOfAssessment) displayAmount = 0;
                  } else if (match) {
                    displayAmount = formatAmount(
                      match.amount,
                      !shouldShowDecimals
                    );
                  }

                  return (
                    <TableCell key={colIdx} align='center'>
                      {displayAmount}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}

            {isS19 && (
              <TableRow key='ia_row'>
                <TableCell sx={iaRowStyle}>Initial Allowance</TableCell>
                <TableCell sx={iaRowStyle}></TableCell>
                <TableCell sx={iaRowStyle}></TableCell>
                {allPrincipalYears.map((year, index) => {
                  if (
                    selectedItem.isCurrentYearDisposed &&
                    year >= yearOfAssessment
                  ) {
                    return (
                      <TableCell key={index} align='center' sx={iaRowStyle}>
                        Forfeit
                      </TableCell>
                    );
                  }
                  const iaForYear = (
                    selectedItem.principalDetails?.[year] || []
                  ).reduce((sum, entry) => sum + (entry.ia || 0), 0);
                  return (
                    <TableCell key={index} align='center' sx={iaRowStyle}>
                      {formatAmount(iaForYear, !shouldShowDecimals)}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            <TableRow key='pp_subtotal'>
              <TableCell sx={totalRowStyle}>
                Sub Total (a){" "}
                <JustInfo title='Heads up: For calculation purposes, the subtotal for each year is displayed as a negative number by default, even though the sum is positive.' />
              </TableCell>

              <TableCell align='center' sx={totalRowStyle}>
                <BalanceVerification
                  grossBalance={totalPrincipalPaid}
                  expectedTotal={selectedItem.cost}
                  matchTitle='The total principal paid amount matches the cost.'
                  notMatchTitle='The total principal paid amount does not match the cost.'
                  balanceAtLeft
                />
              </TableCell>
              {isS19 && (
                <TableCell align='center' sx={totalRowStyle}>
                  {formatAmount(
                    calculateTotalAaClaims(selectedItem.principalDetails),
                    !shouldShowDecimals
                  )}
                </TableCell>
              )}

              {allPrincipalYears.map((year, index) => {
                return (
                  <TableCell key={index} align='center' sx={totalRowStyle}>
                    {selectedItem?.isCurrentYearDisposed &&
                    year > yearOfAssessment
                      ? "Disposed"
                      : formatAmount(
                          yearTotals[year] || 0,
                          !shouldShowDecimals,
                          true
                        )}
                  </TableCell>
                );
              })}
            </TableRow>
            <TableRow key='twdv_subtotal'>
              <TableCell colSpan={2} sx={totalRowStyle}>
                TWDV bf (b)
              </TableCell>
              {isS19 && <TableCell sx={totalRowStyle}></TableCell>}
              {allPrincipalYears.map((year, index) => {
                return (
                  <TableCell key={index} align='center' sx={totalRowStyle}>
                    {selectedItem?.isCurrentYearDisposed &&
                    year > yearOfAssessment
                      ? "Disposed"
                      : formatAmount(
                          twdvTotals[year] || 0,
                          !shouldShowDecimals
                        )}
                  </TableCell>
                );
              })}
            </TableRow>
            <TableRow key='principal_paid'>
              <TableCell colSpan={2} sx={totalRowStyle}>
                Principal paid (c)
              </TableCell>
              {isS19 && <TableCell sx={totalRowStyle}></TableCell>}
              {allPrincipalYears.map((year) => (
                <TableCell key={year} align='center' sx={totalRowStyle}>
                  {selectedItem?.isCurrentYearDisposed &&
                  year > yearOfAssessment
                    ? "Disposed"
                    : formatAmount(
                        principalPaidMap[year] || 0,
                        !shouldShowDecimals
                      )}
                </TableCell>
              ))}
            </TableRow>
            <TableRow key='pp_total'>
              <TableCell colSpan={2} sx={totalRowStyle}>
                TWDV c/f (d) = (a) + (b) + (c)
              </TableCell>
              {isS19 && <TableCell sx={totalRowStyle}></TableCell>}
              {allPrincipalYears.map((year, index) => {
                return (
                  <TableCell key={index} align='center' sx={totalRowStyle}>
                    {selectedItem?.isCurrentYearDisposed &&
                    year > yearOfAssessment
                      ? "Disposed"
                      : formatAmount(
                          finalTotals[year] || 0,
                          !shouldShowDecimals
                        )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default PrincipalPaidTable;
